import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { UnitSearchResult } from '../api/types'
import { unitSearch, type SearchBy } from '../api/bol'
import { apiErrorMessage } from '../lib/apiClient'
import type { MailboxSender } from '../lib/mailbox'

/** How a set of candidate units was arrived at. */
export type ResolvedVia = 'email' | 'name' | 'manual'

export type SenderResolution =
    | { status: 'no-sender' }
    | { status: 'loading' }
    | { status: 'matched'; unit: UnitSearchResult; via: ResolvedVia; alternatives: UnitSearchResult[] }
    | { status: 'choose'; matches: UnitSearchResult[]; via: ResolvedVia }
    | { status: 'not-found' }
    | { status: 'error'; message: string }

type AutoResult = { via: Exclude<ResolvedVia, 'manual'> | 'none'; matches: UnitSearchResult[] }

/**
 * Remembers which unit was picked for a given email address for the life of the
 * pane, so flicking between emails from the same person doesn't ask twice.
 */
const selectionCache = new Map<string, UnitSearchResult>()

function cacheKey(sender: MailboxSender): string {
    return sender.emailAddress.toLowerCase()
}

/**
 * Resolves the sender of an email to a BOL2 unit.
 *
 * Chain: exact email → sender display name → manual search.
 *
 * The email step only matches contacts that have a `user_master` record, because
 * `person_company_master` has no email column and `unit_search` compares
 * `user_master.Email_Id` exactly. Contractors and leaseholders without a portal
 * account therefore fall through to the name search and then to manual lookup —
 * that is expected, not a failure.
 */
export function useSenderResolution(sender: MailboxSender | null) {
    const [selected, setSelected] = useState<UnitSearchResult | null>(null)
    const [manual, setManual] = useState<{ searchBy: SearchBy; filterValue: string } | null>(null)

    // Reset per-sender state, seeding from the cache if we've seen them before.
    useEffect(() => {
        setManual(null)
        setSelected(sender ? selectionCache.get(cacheKey(sender)) ?? null : null)
    }, [sender?.emailAddress])

    const auto = useQuery<AutoResult>({
        queryKey: ['sender-resolution', sender?.emailAddress?.toLowerCase(), sender?.displayName],
        enabled: Boolean(sender) && manual === null,
        staleTime: 5 * 60 * 1000,
        retry: false,
        queryFn: async ({ signal }): Promise<AutoResult> => {
            if (!sender) return { via: 'none', matches: [] }

            const byEmail = await unitSearch(
                { searchBy: 'email', filterValue: sender.emailAddress },
                signal,
            )
            if (byEmail.length > 0) return { via: 'email', matches: byEmail }

            // Only worth trying if the display name is a real name rather than a
            // repeat of the address.
            const name = sender.displayName.trim()
            if (name && name.toLowerCase() !== sender.emailAddress.toLowerCase()) {
                const byName = await unitSearch({ searchBy: 'name', filterValue: name }, signal)
                if (byName.length > 0) return { via: 'name', matches: byName }
            }

            return { via: 'none', matches: [] }
        },
    })

    const manualQuery = useQuery<UnitSearchResult[]>({
        queryKey: ['manual-search', manual?.searchBy, manual?.filterValue],
        enabled: manual !== null,
        staleTime: 5 * 60 * 1000,
        retry: false,
        queryFn: ({ signal }) =>
            unitSearch({ searchBy: manual!.searchBy, filterValue: manual!.filterValue }, signal),
    })

    const active = manual !== null ? manualQuery : auto
    const via: ResolvedVia = manual !== null ? 'manual' : auto.data?.via === 'name' ? 'name' : 'email'
    const matches = useMemo<UnitSearchResult[]>(
        () => (manual !== null ? manualQuery.data ?? [] : auto.data?.matches ?? []),
        [manual, manualQuery.data, auto.data],
    )

    // A single hit needs no disambiguation.
    useEffect(() => {
        if (selected === null && matches.length === 1 && matches[0]) {
            setSelected(matches[0])
        }
    }, [matches, selected])

    const select = useCallback(
        (unit: UnitSearchResult) => {
            setSelected(unit)
            if (sender) selectionCache.set(cacheKey(sender), unit)
        },
        [sender],
    )

    const clearSelection = useCallback(() => {
        setSelected(null)
        if (sender) selectionCache.delete(cacheKey(sender))
    }, [sender])

    const searchManually = useCallback((searchBy: SearchBy, filterValue: string) => {
        setSelected(null)
        setManual({ searchBy, filterValue: filterValue.trim() })
    }, [])

    const resolution = useMemo<SenderResolution>(() => {
        if (!sender) return { status: 'no-sender' }
        if (active.isPending || active.isFetching) return { status: 'loading' }
        if (active.isError) {
            return { status: 'error', message: apiErrorMessage(active.error, 'Could not search Blocks Online') }
        }
        if (selected) {
            return {
                status: 'matched',
                unit: selected,
                via,
                alternatives: matches.filter((match) => match.unit_id !== selected.unit_id),
            }
        }
        if (matches.length > 1) return { status: 'choose', matches, via }
        return { status: 'not-found' }
    }, [sender, active.isPending, active.isFetching, active.isError, active.error, selected, matches, via])

    return {
        resolution,
        manualTerm: manual,
        select,
        clearSelection,
        searchManually,
        refetch: active.refetch,
    }
}
